import { ArrowLeft, Phone as PhoneIcon, Suitcase } from 'iconoir-react'
import { UserIcon } from '@heroicons/react/24/outline'
import { Button } from 'app/components/ui/button'
import { FormInput } from 'app/components/ui/form-input'
import { FormPassword } from 'app/components/ui/form-password'
import { FormSelect } from 'app/components/form/FormComboSelect'
import { SmartForm } from 'app/components/ui/smart-form'
import { AccountSetupErrorScreen } from 'app/components/shared/account-setup/AccountSetupErrorScreen'
import { PasswordRules, TermsFooter } from 'app/components/shared/account-setup/AccountSetupForm'
import { InvalidInvitationScreen } from 'app/components/shared/account-setup/InvalidInvitationScreen'
import { WelcomeSuccessScreen } from 'app/components/shared/account-setup/WelcomeSuccessScreen'
import { POSITION_OPTIONS, GOOGLE_LOGO_URL, type UseActivateReturn } from './useActivate'

export type ActivateShadcnComponentProps = UseActivateReturn

const GOOGLE_PROVIDER = `google`

function ActivateForm({
  email,
  code,
  allowGoogle,
  defaultName,
  defaultPhone,
  defaultPosition,
  position,
  setPosition,
  password,
  setPassword,
  useGoogle,
  setUseGoogle,
  isSubmitting,
  actionError,
  validator,
  onSubmit,
}: {
  email: string
  code: string
  allowGoogle: boolean
  defaultName?: string
  defaultPhone?: string
  defaultPosition?: string
} & Pick<
  UseActivateReturn,
  | 'position'
  | 'setPosition'
  | 'password'
  | 'setPassword'
  | 'useGoogle'
  | 'setUseGoogle'
  | 'isSubmitting'
  | 'actionError'
  | 'validator'
  | 'onSubmit'
>) {
  return (
    <div className="grid h-dvh place-items-center overflow-y-auto bg-surface-subtle p-4">
      <div className="w-full max-w-lg rounded-xl border border-default bg-surface p-8 shadow-sm sm:p-12">
        {!useGoogle && allowGoogle && (
          <Button
            type="button"
            variant="subtle"
            size="icon-sm"
            aria-label="Back"
            className="mb-4"
            onClick={() => setUseGoogle(true)}
          >
            <ArrowLeft />
          </Button>
        )}
        <h1 className="mb-2 text-center text-2xl font-semibold text-strong">Let&apos;s get started</h1>
        <p className="mb-6 text-center text-subtle">Complete your account setup below.</p>

        {useGoogle ? (
          <>
            <form method="post" action={`/auth/${GOOGLE_PROVIDER}`} className="w-full">
              <Button
                variant="tertiary"
                size="lg"
                type="submit"
                loading={isSubmitting}
                disabled={isSubmitting}
                className="w-full"
              >
                <img alt="Google Logo" src={GOOGLE_LOGO_URL} className="mr-2 h-5 w-5" />
                Sign up with Google
              </Button>
            </form>

            <div className="my-8 flex items-center gap-4">
              <div className="h-px flex-1 bg-surface-strong" />
              <span className="text-sm text-subtle">OR</span>
              <div className="h-px flex-1 bg-surface-strong" />
            </div>

            <Button variant="primary" size="lg" className="w-full" onClick={() => setUseGoogle(false)}>
              Sign up with Email
            </Button>
          </>
        ) : (
          <>
            {actionError && (
              <div className="my-4 rounded-lg border border-red-200 bg-red-50 p-3 text-center text-sm text-red-600">
                {actionError}
              </div>
            )}

            <SmartForm
              validator={validator}
              method="post"
              hideButtons={true}
              className="mt-6 flex! w-full flex-col!"
              defaultValues={{
                name: defaultName || '',
                phone: defaultPhone || '',
                position: defaultPosition || '',
              }}
              hiddenInputs={[
                { name: 'email', value: email },
                { name: 'code', value: code },
              ]}
              preventSubmit
              onSubmit={(data) => {
                if (!data) return
                const fd = new FormData()
                fd.append('email', email)
                fd.append('code', code)
                const d = data as Record<string, string>
                for (const key of ['name', 'phone', 'position', 'jobTitle', 'password', 'confirmPassword']) {
                  if (d[key] != null) fd.append(key, d[key])
                }
                onSubmit(fd)
              }}
            >
              <FormInput
                icon={<UserIcon className="h-5 w-5" />}
                label="Name"
                required
                labelClassName="text-sm font-medium text-default"
                className="mb-4 block"
                cols={6}
                type="text"
                name="name"
              />
              <FormInput
                icon={<PhoneIcon className="h-5 w-5" />}
                label="Phone Number"
                labelClassName="text-sm font-medium text-default"
                className="mb-4 block"
                cols={6}
                type="tel"
                name="phone"
                placeholder="Leave blank to authenticate through email only"
              />
              <FormSelect
                icon={<Suitcase className="h-5 w-5" />}
                name="position"
                label="Position"
                labelClassName="text-sm font-medium text-default"
                className="mb-4 block"
                cols={6}
                onChange={(event) => setPosition(event.currentTarget.value as typeof position)}
                options={POSITION_OPTIONS}
                required
              />
              {position === 'Other' && (
                <FormInput
                  name="jobTitle"
                  label="Title"
                  labelClassName="text-sm font-medium text-default"
                  className="mb-4 block"
                  cols={6}
                  autoComplete="off"
                  autoFocus
                  required
                />
              )}
              <FormPassword
                label="Password"
                className="mb-2 flex"
                cols={6}
                name="password"
                placeholder="Enter your password"
                required
                onChange={(e) => setPassword(e.target.value)}
              />
              <PasswordRules password={password} />
              <FormPassword
                label="Repeat Password"
                className="mb-2 flex"
                cols={6}
                name="confirmPassword"
                placeholder="Repeat your password"
                required
              />
              <Button
                variant="primary"
                size="lg"
                type="submit"
                disabled={isSubmitting}
                loading={isSubmitting}
                className="col-span-6 w-full"
              >
                Activate Account
              </Button>
            </SmartForm>
          </>
        )}
        <TermsFooter />
      </div>
    </div>
  )
}

export function ActivateShadcnComponent(props: ActivateShadcnComponentProps) {
  const { state } = props

  if (state.screen === 'success') {
    return <WelcomeSuccessScreen message={state.message} />
  }

  if (state.screen === 'invalid-invitation') {
    return <InvalidInvitationScreen />
  }

  if (state.screen === 'error') {
    return <AccountSetupErrorScreen error={state.error} />
  }

  return (
    <ActivateForm
      email={state.email}
      code={state.code}
      allowGoogle={state.allowGoogle}
      defaultName={state.defaultName}
      defaultPhone={state.defaultPhone}
      defaultPosition={state.defaultPosition}
      position={props.position}
      setPosition={props.setPosition}
      password={props.password}
      setPassword={props.setPassword}
      useGoogle={props.useGoogle}
      setUseGoogle={props.setUseGoogle}
      isSubmitting={props.isSubmitting}
      actionError={props.actionError}
      validator={props.validator}
      onSubmit={props.onSubmit}
    />
  )
}
